import React from 'react';

import Axios from 'axios';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';

import { datePickerToApi, getNow } from '@libs/NwMoment';

import useTransactionLabels from '@hooks/CachedLists/UseTransactionLabels';
import useConfigurations from '@hooks/Configurations/UseConfigurations';
import useFormFieldFocus from '@hooks/Utils/UseFormFieldFocus';

import { NWInput, NWTextAreaCharCounter, NWDatePicker } from '@components/Gui/NwForm/NwFormItems';
import { NwForm } from '@components/Gui/NwForm/NwFormWrapper';
import { ProjectTransactionFormContainer } from '@components/Project/ProjectTransactions/Transactions/ProjectTransactionsUI';

import AmountLine from './Common/AmountLine';
import NwFixedCancelAndSaveBar from '@components/Gui/NwFixedCancelAndSaveBar';
import ServiceLine from './Common/ServiceLine';
import TransactionLabelsSelector from './Common/TransactionLabelsSelector';

const ExpenseFormContact = ({
    accommodationID,
    amount,
    contactID,
    statementType,
    description,
    onCloseDrawer,
    onTransactionCreated,
    serviceID,
    title,
    width,
    minWidth,
}) => {
    //onChangeForm
    useFormFieldFocus('Title');
    const transactionProperties = useTransactionLabels('Expense');
    const defaultTypeQuantity = transactionProperties.startLabel.TypeQuantityName
        ? transactionProperties.startLabel.TypeQuantityName
        : 'Units';
    const configurations = useConfigurations();

    const saveTransaction = async (values, setSubmitting) => {
        let apiData = {
            TransactionLabelID: values.TransactionLabelID,
            Title: values.Title,
            Description: values.Description,
            Quantity: values.Quantity[0],
            TypeQuantity: values.Quantity[1],
            Currency: values.Amount[0],
            SingleFee: values.Amount[1],
            RefDate: values.RefDate && datePickerToApi(values.RefDate),
            Contacts: [
                {
                    ContactID: contactID,
                    ContactBillingInfoID: null,
                    AccommodationID: accommodationID,
                },
            ],
        };

        apiData.StatementType = statementType;

        if (values.ServiceID && !(statementType === 'Service')) {
            apiData.ServiceID = values.ServiceID;
            if (values.AgencyCost) {
                apiData.AgencyCost = values.AgencyCost[1];
            } else {
                apiData.AgencyCost = values.SingleFee[1];
            }
        }

        try {
            await Axios.post('/transactions/expense', apiData);
            onTransactionCreated();
            onCloseDrawer();
            setSubmitting(false);
        } catch (error) {
            console.error('Create Contact Expense Error => ', error);
            setSubmitting(false);
        }
    };

    const initialValues = {
        TransactionLabelID: transactionProperties.startLabel.ID,
        Title: title ? title : transactionProperties.startLabel.Label,
        ServiceID: serviceID ? serviceID : null,
        Description: description ? description : null,
        Amount: [configurations.defaultCurrency, amount ? amount : null],
        AgencyCost: [configurations.defaultCurrency, amount && serviceID ? amount : null],
        Quantity: [1, defaultTypeQuantity],
        RefDate: getNow(),
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={Yup.object().shape({
                Title: Yup.string().required('Title is required'),
                TransactionLabelID: Yup.string().required('Label is required'),
            })}
            validateOnBlur={true}
            validateOnChange={false}
            onSubmit={(values, { setSubmitting }) => {
                setSubmitting(true);
                saveTransaction(values, setSubmitting);
            }}
        >
            {({ isSubmitting, values, setFieldValue, handleSubmit, touched, isValid }) => (
                <NwForm
                    values={values}
                    //onTouchForm={onChangeForm}
                    onFinish={handleSubmit}
                    layout="vertical"
                >
                    <ProjectTransactionFormContainer>
                        <TransactionLabelsSelector
                            values={values}
                            setFieldValue={setFieldValue}
                            transactionProperties={transactionProperties}
                            updateTitle
                        />
                        <Field
                            component={NWDatePicker}
                            label="Date"
                            name="RefDate"
                            format="MMM DD, YYYY"
                            allowClear={false}
                            value={values.RefDate}
                        />
                        <Field
                            component={NWInput}
                            label="Title"
                            name="Title"
                            type="text"
                            value={values.Title}
                            maxLength={255}
                        />
                        <Field
                            component={NWTextAreaCharCounter}
                            label="Description"
                            name="Description"
                            type="text"
                            value={values.Description}
                            maxLength={2500}
                            autoSize={{ minRows: 3, maxRows: 10 }}
                        />
                        <AmountLine
                            values={values}
                            touched={touched}
                            onChangeAmount={(value) => {
                                if (!touched.AgencyCost) {
                                    setFieldValue('AgencyCost', [value[0], value[1]]);
                                }
                            }}
                            amountLabel="Expense Amount"
                        />
                        <ServiceLine values={values} setFieldValue={setFieldValue} hasAgencyCost />
                    </ProjectTransactionFormContainer>
                    <NwFixedCancelAndSaveBar
                        isSubmitting={isSubmitting}
                        isValid={isValid}
                        onCancel={onCloseDrawer}
                        width={width}
                        minWidth={minWidth}
                    />
                </NwForm>
            )}
        </Formik>
    );
};

export default ExpenseFormContact;
